// Madson: <auto-correct-variant>
// Self-healing custom element. Emitted by variant-main-picker.liquid when the URL's
// option_values combo doesn't match any product variant (e.g. Black/Green/Polarized when
// Green Mirror only ships Enhanced). Liquid auto-flipped the picker UI to the cheapest
// valid combo, but the gallery + browser URL still reflect the bad combo. We re-fetch
// with ?variant=<final_id> to fix gallery + URL + anything else that reads selected_variant.

import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * Variant ids we have already corrected, at MODULE scope.
 *
 * This guard cannot live on the element. The correction replaces the whole
 * <variant-picker>, and <auto-correct-variant> is emitted inside it, so the
 * replacement delivers a BRAND NEW element with an empty dataset. A per-element
 * `data-correcting` flag is therefore never observed by the element that would
 * need to honour it, and every correction re-entered immediately.
 *
 * Measured on the t-shirt PDP before this fix: 34 gallery rebuilds in the first
 * ~9 seconds (each VariantUpdateEvent makes media-gallery.js replaceWith the
 * whole gallery). During that window the carousel could not be scrolled and the
 * variant picker flickered, because every element the shopper touched was
 * detached mid-interaction.
 */
const correctedVariantIds = new Set();

class AutoCorrectVariant extends HTMLElement {
  connectedCallback() {
    const variantId = this.dataset.variantId;
    const productUrl = this.dataset.productUrl;
    if (!variantId || !productUrl) return;

    // Already corrected to this variant in this pageview.
    if (correctedVariantIds.has(variantId)) return;

    // The URL already points at this variant, so there is nothing left to correct.
    // This also catches the re-entry case, because the first pass calls
    // history.replaceState with exactly this id before dispatching.
    if (new URLSearchParams(window.location.search).get('variant') === variantId) {
      correctedVariantIds.add(variantId);
      return;
    }

    correctedVariantIds.add(variantId);

    const correctedUrl = `${productUrl}?variant=${variantId}`;

    fetch(correctedUrl)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        // Update browser URL to ?variant=<final_id> so refresh / share works
        const u = new URL(window.location.href);
        u.searchParams.set('variant', variantId);
        u.searchParams.delete('option_values');
        history.replaceState({}, '', u.toString());

        // Read the corrected variant JSON for the VariantUpdateEvent payload
        const variantJsonText = html.querySelector(
          'variant-picker script[type="application/json"]'
        )?.textContent;
        if (!variantJsonText) return;

        const variant = JSON.parse(variantJsonText);

        // Replace the variant-picker element with the corrected one (radios + JSON)
        const newPicker = html.querySelector('variant-picker');
        const oldPicker = document.querySelector('variant-picker');
        if (newPicker && oldPicker) {
          // Strip any corrector the corrected render still carries. The module-scoped
          // guard above already stops re-entry; this keeps the dead element out of the
          // DOM entirely so nothing can revive the loop.
          newPicker.querySelectorAll('auto-correct-variant').forEach((el) => el.remove());
          oldPicker.replaceWith(newPicker);
        }

        // Dispatch VariantUpdateEvent so media-gallery, product-form, etc. update.
        // event.detail.data.html is consumed by media-gallery.js #handleVariantUpdate
        // to swap the gallery wholesale.
        const sectionId = newPicker?.dataset.sectionId || '';
        const variantPickerForEvent = document.querySelector('variant-picker');
        if (variantPickerForEvent) {
          variantPickerForEvent.dispatchEvent(
            new VariantUpdateEvent(variant, sectionId, {
              html,
              productId: variantPickerForEvent.dataset.productId ?? '',
            })
          );
        }
      })
      .catch((err) => {
        console.warn('[auto-correct-variant] correction fetch failed', err);
      });
  }
}

if (!customElements.get('auto-correct-variant')) {
  customElements.define('auto-correct-variant', AutoCorrectVariant);
}
